import { ref, computed, unref } from 'vue';
import store from '@/store';
import { config } from '@/config';
import { useToast } from '@/composables/common/useToast';

const METODOS_PAGO_EXCLUIDOS = [5, 8, 9];

const FILTROS_DEFAULT = {
    per_page: 999,
    page: 1,
    search: '',
    all: 1
};

/**
 * useCambiarPrecioLogic
 *
 * Composable que encapsula la lógica del modal "Cambiar tipo de abono y precio".
 * Mantiene el estado del formulario, carga los tipos de abonos desde el backend
 * y dispara el cambio de tipo de abono con feedback al usuario.
 *
 * Acepta teatroRef y listaIdRef opcionales para permitir testear el composable
 * sin tocar el store. Si no se pasan, se leen de `store.getters`.
 *
 * @param {Object} abonado - objeto abonado con al menos { id, balance }
 * @param {Ref|Object|null} teatroRef - ref opcional al teatro
 * @param {Ref|Object|null} listaIdRef - ref opcional al lista_id
 * @param {Object} options - opciones
 * @param {Function} options.onSuccess(cambiado) - callback tras un cambio exitoso
 * @returns {Object} API pública del composable
 */
export function useCambiarPrecioLogic(abonado, teatroRef = null, listaIdRef = null, options = {}) {
    const toast = useToast();

    const teatro = computed(() => {
        if (teatroRef !== null && teatroRef !== undefined) {
            return unref(teatroRef);
        }
        return store.getters.getTeatro;
    });

    const listaId = computed(() => {
        if (listaIdRef !== null && listaIdRef !== undefined) {
            return unref(listaIdRef);
        }
        return store.getters.getListaId;
    });

    // ==================== STATE ====================
    const tipos = ref([]);
    const loading = ref(false);
    const tipo_abono = ref(null);
    const precio = ref(null);
    const metodo_pago = ref(null);
    const metodos_pago = ref(null);
    const filters = ref({ ...FILTROS_DEFAULT });

    // ==================== COMPUTED ====================
    const tiposOptions = computed(() =>
        tipos.value.map(t => ({
            ...t,
            _uid: t.id,
            _label: (t.renovacion ? '(Renovación) - ' : '') + (t.descripcion || t.nota || '')
        }))
    );

    const precios_abono = computed(() => {
        if (tipo_abono.value !== null && tipo_abono.value !== undefined) {
            const tipoSeleccionado = tiposOptions.value.find(t => t._uid === tipo_abono.value);
            return tipoSeleccionado?.descuentos || [];
        }
        return [];
    });

    const preciosOptions = computed(() =>
        precios_abono.value.map(p => ({
            ...p,
            _uid: p.id,
            _label: p.label_taquilla || ''
        }))
    );

    const cambio = computed(() => {
        if (precio.value !== null && precio.value !== undefined) {
            const selectedPrecio = precios_abono.value.find(p => p.id === precio.value);
            if (selectedPrecio) {
                return selectedPrecio.precio_total_taquilla - abonado.balance;
            }
        }
        return 0;
    });

    const metodosPagoDisponibles = computed(() => {
        if (!teatro.value) {
            return [];
        }
        const lista = teatro.value.metodos_pago_abonos_bases || [];
        return lista.filter(m => !METODOS_PAGO_EXCLUIDOS.includes(m.id));
    });

    // ==================== METHODS ====================
    async function getTipos() {
        loading.value = true;
        try {
            const params = {
                filters: filters.value,
                lista_id: listaId.value
            };
            const res = await store.dispatch(
                config.getVuexAlias('back.precios.index'),
                params
            );
            tipos.value = res.data;
        } catch (err) {
            console.error('Error fetching tipos de abonos:', err);
            toast.error('Error al cargar los tipos de abonos');
        } finally {
            loading.value = false;
        }
    }

    async function cambiar() {
        loading.value = true;
        try {
            const tipoSeleccionado = tipo_abono.value !== null && tipo_abono.value !== undefined
                ? tipos.value.find(t => t.id === tipo_abono.value)
                : null;
            const precioSeleccionado = precio.value !== null && precio.value !== undefined
                ? precios_abono.value.find(p => p.id === precio.value)
                : null;

            if (!tipoSeleccionado || !precioSeleccionado) {
                toast.error('Selecciona tipo de abono y precio');
                return;
            }

            const params = {
                id: abonado.id,
                abonado_id: abonado.id,
                descuento: precioSeleccionado,
                tipo_abono: tipoSeleccionado,
                metodo_pago: metodo_pago.value
            };

            await store.dispatch(
                config.getVuexAlias('back.abonados.tipoabono.editar'),
                params
            );

            toast.success('Tipo de abono actualizado');
            if (options.onSuccess) {
                options.onSuccess(true);
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Error al cambiar el tipo de abono';
            toast.error(errorMessage);
        } finally {
            loading.value = false;
        }
    }

    function reset() {
        tipo_abono.value = null;
        precio.value = null;
        metodo_pago.value = null;
    }

    // ==================== PUBLIC API ====================
    return {
        // state
        tipos,
        loading,
        tipo_abono,
        precio,
        metodo_pago,
        metodos_pago,
        filters,
        // computed
        tiposOptions,
        precios_abono,
        preciosOptions,
        cambio,
        metodosPagoDisponibles,
        teatro,
        // methods
        getTipos,
        cambiar,
        reset
    };
}
